/**
 * Button.tsx — Componente referencia canónica del design system
 *
 * Patrón: Radix Slot (asChild) + CVA (variantes/sizes) + Tailwind + Lucide.
 * Este componente es la plantilla que siguen Input, Card, Modal, etc.
 *
 * Por qué Radix Slot:
 *   Permite que el consumidor pase asChild={true} para renderizar el
 *   Button como otro elemento (ej. <a>, <Link> de react-router) sin
 *   perder las props de estilo y accesibilidad.
 *
 * Por qué CVA:
 *   Elimina la lógica condicional de clases en el JSX y fuerza variantes
 *   exhaustivas y tipadas. El bundle de CVA es ~2 KB gzip (ADR-025).
 *
 * Por qué Lucide Loader2:
 *   Reemplaza el SVG inline del Día 1. Lucide es tree-shakeable, MIT, y
 *   garantiza íconos consistentes en 24px con stroke uniforme (ADR-027).
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from './utils';

// ---------------------------------------------------------------------------
// Variantes con CVA — tokens cumple-* (migrado desde brand-* Día 1)
// ---------------------------------------------------------------------------

const buttonVariants = cva(
  // Base: aplica a todas las variantes.
  // min-h/min-w ≥ 44px garantiza área táctil WCAG 2.5.5 (tablet-first 768px).
  [
    'inline-flex items-center justify-center gap-2',
    'font-medium whitespace-nowrap select-none',
    'rounded-md border border-transparent',
    'transition-colors duration-150',
    // Focus ring WCAG AA: 2px offset + 2px ring en color accent (cumple-accent-500 = #0EA5E9).
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cumple-accent-500 focus-visible:ring-offset-2',
    // Disabled: reduce opacidad + bloquea eventos. aria-disabled se aplica via prop.
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
    // Área táctil mínima WCAG 2.5.5
    'min-h-[44px] min-w-[44px]',
  ],
  {
    variants: {
      variant: {
        /**
         * primary — CTA principal. Fondo sólido cumple-primary-700.
         * hover: primary-600 (#5B3FF2) — más saturado, contraste 8.76:1 AAA.
         * active: primary-900 (#0F172A) — oscuro, 15.75:1 AAA.
         */
        primary: [
          'bg-cumple-primary-700 text-white',
          'hover:bg-cumple-primary-600',
          'active:bg-cumple-primary-900',
        ],
        /**
         * secondary — Acción alternativa. Borde + texto cumple-primary-700, fondo transparente.
         * hover/active: cumple-primary-700 semitransparente (8%/15%) sobre blanco.
         * SH-1: migramos de los rgba hardcodeados a la notación /[N] de Tailwind,
         * que funciona correctamente con las CSS variables canal "R G B" de ADR-029.
         */
        secondary: [
          'border-cumple-primary-700 text-cumple-primary-700 bg-transparent',
          'hover:bg-cumple-primary-700/[.08]',
          'active:bg-cumple-primary-700/[.15]',
        ],
        /**
         * ghost — Acción sutil. Sin borde visible en reposo.
         */
        ghost: [
          'text-cumple-primary-700 bg-transparent border-transparent',
          'hover:bg-cumple-primary-700/[.08]',
          'active:bg-cumple-primary-700/[.15]',
        ],
        /**
         * danger — Acción destructiva. Rojo semántico cumple-danger-*.
         */
        danger: [
          'bg-cumple-danger-500 text-white border-transparent',
          'hover:bg-cumple-danger-600',
          'active:bg-cumple-danger-700',
        ],
        /**
         * link — Inline como anchor. Sin fondo ni borde; underline al hover.
         * min-h/min-w se mantienen para área táctil, pero no se aplica padding.
         */
        link: [
          'text-cumple-primary-700 bg-transparent border-transparent underline-offset-4',
          'hover:underline hover:text-cumple-primary-600',
          'active:opacity-80',
        ],
      },
      size: {
        sm: 'h-9 px-3 text-sm min-h-[44px]',
        md: 'h-11 px-4 text-base',
        lg: 'h-13 px-6 text-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

// ---------------------------------------------------------------------------
// Props del componente
// ---------------------------------------------------------------------------

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /**
   * asChild — Cuando es true, el componente fusiona sus props con el único
   * hijo que reciba. Útil para renderizar como <a> o <Link> de react-router
   * sin envolver el DOM (patrón Radix Slot).
   *
   * @example
   *   <Button asChild variant="primary">
   *     <a href="/dashboard">Ir al dashboard</a>
   *   </Button>
   */
  asChild?: boolean;
  /**
   * loading — Muestra spinner Lucide Loader2 y aplica aria-busy="true". El botón sigue
   * siendo interactuable visualmente (no disabled) a menos que se combine
   * con disabled={true}, porque "cargando" no siempre debe bloquear acción.
   */
  loading?: boolean;
  /**
   * loadingText — Texto accesible anunciado por lectores de pantalla cuando
   * loading=true. Si no se provee, el lector anuncia el children original.
   */
  loadingText?: string;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      loadingText,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // aria-disabled refleja el estado visual de disabled SIN bloquear el foco
    // en tabIndex — esto es correcto para WCAG 4.1.2 cuando se necesita que
    // un botón disabled sea descubrible por AT pero no activable.
    const isDisabled = disabled ?? false;
    const isLoading = loading && !isDisabled;

    // Cuando asChild=true, Radix Slot requiere exactamente un hijo React.
    // Los estados loading/disabled no se aplican en modo asChild porque el
    // elemento hijo (ej. <a>) controla su propio DOM. Se fusionan solo las
    // clases de estilo y los datos ARIA relevantes.
    if (asChild) {
      return (
        <Comp
          ref={ref}
          className={cn(buttonVariants({ variant, size }), className)}
          aria-disabled={isDisabled || undefined}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        // Atributos ARIA de estado
        aria-disabled={isDisabled || undefined}
        aria-busy={isLoading || undefined}
        // El disabled nativo bloquea submit de forms; lo mantenemos para HTML semántico.
        disabled={isDisabled}
        {...props}
      >
        {isLoading ? (
          <>
            {/* Lucide Loader2: tree-shakeable, stroke uniforme 24px (ADR-027) */}
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {/* Texto accesible alternativo en estado loading */}
            {loadingText ? <span>{loadingText}</span> : children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
